import { Consumer, createConsumer } from "@rails/actioncable"
import { captureException } from "@sentry/react"
import { auth } from "auth"
import { invalidateAllQueries } from "bonzai"
import { useConfettiBannerStore } from "../components/ConfettiBanner/store"
import { BACKOFFICE_API_URL, IS_DEV_BUILD } from "../env"

// Connection state tracking
type ConnectionState =
  | "connecting"
  | "connected"
  | "disconnected"
  | "rejected"
  | "failed"

type ActionCableManager = {
  consumer: Consumer | null
  state: ConnectionState
  authFailureCount: number
  lastConnectionAttempt: Date | null
  isAuthenticationFailed: boolean
  reconnectTimeoutId: NodeJS.Timeout | null
  maxRetryAttempts: number
}

// Global state for ActionCable connection
const actionCableManager: ActionCableManager = {
  consumer: null,
  state: "connecting",
  authFailureCount: 0,
  lastConnectionAttempt: null,
  isAuthenticationFailed: false,
  reconnectTimeoutId: null,
  maxRetryAttempts: 3,
}

// Environment-aware logging helper
const debugLog = (message: string, ...args: unknown[]) => {
  if (IS_DEV_BUILD) {
    console.log(`[ActionCable] ${message}`, ...args)
  }
}

const warnLog = (message: string, ...args: unknown[]) => {
  if (IS_DEV_BUILD) {
    console.warn(`[ActionCable] ${message}`, ...args)
  }
}

// Helper function to update connection state (minimal logging)
const updateConnectionState = (newState: ConnectionState) => {
  actionCableManager.state = newState
  actionCableManager.lastConnectionAttempt = new Date()
}

// Helper function to clear any existing reconnect timeout
const clearReconnectTimeout = () => {
  if (actionCableManager.reconnectTimeoutId) {
    clearTimeout(actionCableManager.reconnectTimeoutId)
    actionCableManager.reconnectTimeoutId = null
  }
}

// Helper function to schedule reconnection with delay
const scheduleReconnection = () => {
  // Don't schedule if we've exceeded max attempts
  if (
    actionCableManager.authFailureCount >= actionCableManager.maxRetryAttempts
  ) {
    debugLog(
      `Maximum retry attempts (${actionCableManager.maxRetryAttempts}) reached`
    )
    return
  }

  // Clear any existing timeout
  clearReconnectTimeout()

  // Calculate delay: 5 seconds for first retry, then exponential backoff
  const baseDelay = 5000 // 5 seconds
  const delay = baseDelay * Math.pow(2, actionCableManager.authFailureCount - 1)

  debugLog(
    `Scheduling reconnection attempt ${
      actionCableManager.authFailureCount + 1
    }/${actionCableManager.maxRetryAttempts} in ${delay}ms`
  )

  actionCableManager.reconnectTimeoutId = setTimeout(() => {
    debugLog(
      `Attempting reconnection (attempt ${
        actionCableManager.authFailureCount + 1
      })`
    )
    initializeActionCable()
  }, delay)
}

/**
 * An action cable is a web socket connection to our Ruby back-end
 * It's based on a library unique to Ruby and is intended to facilitate real-time communication between a front-end and a Ruby back-end.
 * The connection is currently used to receive messages regarding a playthrough that has been processed and we use it for firing confetti and updating the FE
 */
export const initializeActionCable = async () => {
  try {
    // Prevent multiple simultaneous initializations, but allow reconnection attempts
    // Reconnection attempts are identified by having an authentication failure flag set
    if (
      actionCableManager.state === "connecting" &&
      actionCableManager.consumer &&
      !actionCableManager.isAuthenticationFailed
    ) {
      debugLog("Initialization already in progress, skipping")
      return
    }

    // Clear any existing consumer and timeouts
    if (actionCableManager.consumer) {
      try {
        actionCableManager.consumer.disconnect()
        debugLog("Disconnected existing consumer for fresh initialization")
      } catch (error) {
        warnLog("Error disconnecting existing consumer:", error)
      }
    }
    clearReconnectTimeout()

    const isReconnectionAttempt = actionCableManager.isAuthenticationFailed
    updateConnectionState("connecting")
    debugLog(
      isReconnectionAttempt
        ? "Reconnecting with fresh authentication"
        : "Initializing ActionCable"
    )

    const url = await auth.addAccessParams(`${BACKOFFICE_API_URL}/cable`)
    debugLog(`Generated ${isReconnectionAttempt ? "fresh " : ""}WebSocket URL`)

    // Setup web socket connection
    const consumer = createConsumer(url)

    // Store consumer instance globally
    actionCableManager.consumer = consumer

    if (consumer === null || consumer === undefined) {
      const errorMessage =
        "An Action Cable web socket instance does not exist. This should never happen."
      console.error(`[ActionCable] ${errorMessage}`) // Always log critical errors
      updateConnectionState("failed")
      captureException(new Error(errorMessage))
      return
    }

    // Run call back when receiving communication on the channel from back-end
    consumer.subscriptions.create("PlaythroughProcessedChannel", {
      connected() {
        updateConnectionState("connected")
        debugLog("Connected to PlaythroughProcessedChannel")
        // Reset failure counters on successful connection
        actionCableManager.authFailureCount = 0
        actionCableManager.isAuthenticationFailed = false
        // Clear any pending reconnection attempts
        clearReconnectTimeout()
      },

      disconnected() {
        updateConnectionState("disconnected")
        debugLog("Disconnected from PlaythroughProcessedChannel")
      },

      rejected() {
        updateConnectionState("rejected")
        actionCableManager.authFailureCount++
        actionCableManager.isAuthenticationFailed = true

        // Only log in development to prevent spam
        debugLog(
          `Authentication failed (attempt ${actionCableManager.authFailureCount}). Disconnecting consumer.`
        )

        // Disconnect consumer to prevent continuous ping attempts with invalid credentials
        if (actionCableManager.consumer) {
          try {
            actionCableManager.consumer.disconnect()
            updateConnectionState("disconnected")
          } catch (disconnectError) {
            // Always log critical errors to Sentry
            console.error(
              "[ActionCable] Error disconnecting consumer:",
              disconnectError
            )
            captureException(disconnectError)
          }
        }

        // Schedule reconnection with delay if we haven't exceeded max attempts
        scheduleReconnection()
      },

      received: async () => {
        const { updateCurrentProductProgress } =
          useConfettiBannerStore.getState()
        updateCurrentProductProgress()

        invalidateAllQueries()
      },
    })

    updateConnectionState("connecting")
    debugLog("Consumer created, waiting for connection")
  } catch (error) {
    updateConnectionState("failed")
    // Always log critical errors
    console.error("[ActionCable] Failed to initialize:", error)
    captureException(error)
  }
}

// Export function to get current connection state (for debugging/monitoring)
export const getActionCableState = () => ({
  ...actionCableManager,
  // Don't expose the actual consumer to prevent misuse
  consumer: actionCableManager.consumer ? "initialized" : null,
})

// Export function to get consumer instance (for controlled access)
export const getActionCableConsumer = () => actionCableManager.consumer

// Export function to manually trigger reconnection (useful for debugging or user-initiated retry)
export const reconnectActionCable = () => {
  debugLog("Manual reconnection triggered")

  // Reset failure count to allow reconnection
  actionCableManager.authFailureCount = 0
  actionCableManager.isAuthenticationFailed = false

  // Clear any pending timeouts
  clearReconnectTimeout()

  // Trigger reconnection
  initializeActionCable()
}
